import { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { ContextDataProvider } from "./v2/contexts/ContextDataProvider";
import WidgetErrorBoundary from "./components/WidgetErrorBoundary";
import OfflineBanner from "./components/OfflineBanner";
import MiniLoader from "./components/MiniLoader";
import useOfflineDetection from "./hooks/useOfflineDetection";
import lazyWithRetry from "./utils/lazyWithRetry";

const ChatProviderWrapper = lazyWithRetry(() =>
  import("./v2/contexts/ChatProviderWrapper").then((m) => ({
    default: m.ChatProviderWrapper,
  })),
);
const ErrorPage = lazyWithRetry(() => import("./components/ErrorPage"));
const TextMeView = lazyWithRetry(() => import("./views/TextMe"));
const Popup = lazyWithRetry(() => import("./components/Popup/Popup"));
const ChatLayout = lazyWithRetry(
  () => import("./v2/components/Chat/ChatLayout"),
);
const CtaView = lazyWithRetry(() => import("./views/CtaView"));
const MobileCoupon = lazyWithRetry(() => import("./views/MobileCoupon"));
const DesktopCoupon = lazyWithRetry(() => import("./views/DesktopCoupon"));
const MobileCta = lazyWithRetry(() => import("./views/MobileCta"));
const SubscriberGrowth = lazyWithRetry(
  () => import("./views/SubscriberGrowth"),
);
const WizLayout = lazyWithRetry(
  () => import("./v2/components/Chat/Wiz/WizLayout"),
);
const SearchBarLayout = lazyWithRetry(
  () => import("./v2/components/Chat/SearchBar/SearchBarLayout"),
);
const ConnectV2 = lazyWithRetry(() => import("./connect-v2/ConnectV2"));
const AscDemo = lazyWithRetry(() => import("./views/demo/AscDemo"));
const Inventory = lazyWithRetry(() => import("./views/demo/Inventory"));
const Details = lazyWithRetry(() => import("./views/demo/Details"));
const CarDealershipDemo = lazyWithRetry(() => import("./views/demo"));
const DemoWidget = lazyWithRetry(() => import("./views/demo/DemoWidget"));
const ConfigLayout = lazyWithRetry(
  () => import("./v2/components/ConfigMode/ConfigLayout"),
);

function ChatRoute({ children }) {
  const isOffline = useOfflineDetection();
  const { lang } = useParams();
  return (
    <>
      {isOffline && <OfflineBanner lang={lang} />}
      <Suspense fallback={<MiniLoader />}>
        <ChatProviderWrapper>{children}</ChatProviderWrapper>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ContextDataProvider>
        <Router>
          <Suspense fallback={<MiniLoader />}>
            <WidgetErrorBoundary>
              <Routes>
                <Route path="/coupon/:token/:lang" element={<MobileCoupon />} />
                <Route
                  path="/mobile-coupon/:token/:lang"
                  element={<MobileCoupon />}
                />
                <Route
                  path="/desktop-coupon/:token/:lang"
                  element={<DesktopCoupon />}
                />
                <Route
                  path="/chat_widget/:theme=default/:feature=text-me/:token/:lang/:locationId?"
                  element={<TextMeView theme="default" />}
                />
                <Route path="/cta/:locationId/:lang" element={<CtaView />} />
                <Route path="/popup/:token/:lang" element={<Popup />} />
                <Route
                  path="/mobile-cta/:token/:lang"
                  element={<MobileCta />}
                />
                <Route
                  path="/subscriber-growth/:token/:lang"
                  element={<SubscriberGrowth />}
                />
                <Route
                  path="/chat_widget/:theme=default/:token/:lang"
                  element={
                    <ChatRoute>
                      <ChatLayout theme="default" />
                    </ChatRoute>
                  }
                />
                <Route
                  path="/wiz_widget/:theme=default/:token/:lang"
                  element={
                    <ChatRoute>
                      <WizLayout theme="default" />
                    </ChatRoute>
                  }
                />
                <Route
                  path="/search_widget/:theme=default/:token/:lang"
                  element={
                    <ChatRoute>
                      <SearchBarLayout />
                    </ChatRoute>
                  }
                />
                <Route
                  path="/connect_v2_widget/:theme=default/:token/:lang"
                  element={
                    <ChatRoute>
                      <ConnectV2 />
                    </ChatRoute>
                  }
                />
                <Route
                  path="/connect_v2_search_widget/:theme=default/:token/:lang"
                  element={
                    <ChatRoute>
                      <ConnectV2 searchBar />
                    </ChatRoute>
                  }
                />
                <Route
                  path="/config_mode/:theme=default/:token/:lang"
                  element={
                    <Suspense fallback={<div></div>}>
                      <ConfigLayout />
                    </Suspense>
                  }
                />
                <Route path="/demo-inventory" element={<Inventory />} />
                <Route path="/demo-details" element={<Details />} />
                <Route path="/asc-demo" element={<AscDemo />} />
                <Route path="/demo-widget" element={<DemoWidget />} />

                <Route path="/demo" element={<CarDealershipDemo />} />
                <Route path="*" element={<ErrorPage />} />
              </Routes>
            </WidgetErrorBoundary>
          </Suspense>
        </Router>
      </ContextDataProvider>
    </Provider>
  );
}

export default App;
